import { arraysAreEqual } from "../../../../components-shared/utils/array-utils.js";
import { html } from 'lit-html';
import { repeat } from 'lit-html/directives/repeat';
import { IProductWithVariants, IVariant, IVariantType } from './variant-selector-base.js';
import { UnlistedListVariantSelectorBase } from "./unlisted-list-variant-selector-base.js";
import { ProductApi } from "../../../api/archive/product/product-api.js"
import { IVariantShortInfoResponse } from '../../../api/archive/product/interfaces/i-variant-short-info-response.js';

const $ = window.jQuery;

export class DropDownVariantSelectorV4 extends UnlistedListVariantSelectorBase {
    private IMAGE_WIDTH = 56;
    private IMAGE_MODE = "crop=none";
    private _variantTypeToCurrentValueMap: Map<number, number>;
    private _isOneTypeVariant: boolean = false;
    private _productApi: ProductApi = new ProductApi();
    private _variantsShortInfo: IVariantShortInfoResponse;
    private _valuesToFill: Array<Array<number>> = new Array<Array<number>>();

    constructor(name: string, selectorContainerElement: HTMLElement, product: IProductWithVariants, variantTypeId: number, translations: Map<string, string>, showPricesInclVat: boolean, loadAdditionalInfo: boolean) {
        super(name, 1, selectorContainerElement, product, variantTypeId, translations, showPricesInclVat, loadAdditionalInfo);

        this._variantTypeToCurrentValueMap = new Map(this.product.variantTypes.map((variantType) => [variantType.id, 0]));
        this._isOneTypeVariant = this.product.variantTypes.length <= 1;

        $(document).on(this.VARIANT_CHANGED_EVENT, this._onVariantChanged.bind(this));
    }

    protected template = () => html`
        <label class="c-variant-selector__label" for="${this.variantSelectorData.id}">${this.variantSelectorData.variants.length > 1 ? this.variantSelectorData.labels.select : '' } ${this.variantSelectorData.variantTypeName}</label>
        <div class="dropdown">
            <button class="dropdown-toggle" type="button" id="dropdown-${this.variantSelectorData.id}" data-bs-offset="0,0" data-bs-display="static" data-bs-toggle="dropdown" aria-expanded="false" data-toggle="dropdown"  aria-haspopup="true" aria-expanded="false">
                ${this.variantSelectorData.labels.select}...
            </button>
            <ul id="${this.variantSelectorData.id}" class="dropdown-menu" aria-labelledby="dropdown-${this.variantSelectorData.id}">
                ${repeat(this.variantSelectorData.variants, (variant:IVariant) => html`
                    <li>
                        <button class="dropdown-item" data-value="${variant.id}" data-name="${variant.name}" type="button">
                        ${variant.name}
                        </button>
                    </li>`
                )}
            </ul>
        </div>`;

     protected getDataBeforeRender() {
        return {
            id: new Date().valueOf() + '_vt' + this.variantType.id,
            variantTypeName: this.variantType.name.toLowerCase(),
            variants: this.sortVariants(this.selectorContainerElement, this.variantType.variants, this.product),
            labels: { select: this.translations.get('selectVariantPrefix'), changeSelection: this.translations.get('changeSelectionVariantPrefix') }
        };
    }

    protected processAfterRender() {
        super.processAfterRender();
        const self = this;

        if(this.loadAdditionalInfo){
            this._productApi.getVariantsShortInfo(this.product.productId).then(response => {
                self._variantsShortInfo = response;
                if(self._isOneTypeVariant){
                    self._fillVariantsInfo();
                } else {
                    self._checkValuesToFill();
                }
            });
        } else {
            // wait until render
            const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
            sleep(500).then(() => {
                if(self._isOneTypeVariant){
                    self._fillVariantsInfo();
                } else {
                    self._checkValuesToFill();
                }
            });
        }
    }

    private _onVariantChanged(evt: CustomEvent, variantType: IVariantType, newVariantValue: number, isSelectedOptionAvailable: boolean, productId: number){
        if(this.product.productId !== productId || this._isOneTypeVariant || !this._variantTypeToCurrentValueMap.has(variantType.id)){
            return;
        }

        this._variantTypeToCurrentValueMap.set(variantType.id, newVariantValue);
        const otherVariantTypeToValueMap = new Map([...this._variantTypeToCurrentValueMap.entries()].filter(x => x[0] !== this.variantType.id));
        const allOtherValuesSelected = [...otherVariantTypeToValueMap.values()].every(variantValue => variantValue > 0);

        if(allOtherValuesSelected){
            this._fillVariantsInfo([...otherVariantTypeToValueMap.values()]);
        }
    }

    private _fillVariantsInfo(otherSelectedValues: Array<number> = null): void {
        if(this.loadAdditionalInfo && !this._variantsShortInfo){
            this._valuesToFill.push(otherSelectedValues);
            return;
        }

        const select = this.selectorContainerElement.querySelector('ul') as unknown as HTMLSelectElement;

        for (const option of select.querySelectorAll('button')) {
            const selectedVariantValue = parseInt(option.dataset.value);
            if(!selectedVariantValue){
                continue;
            }

            const selectedVariantValues = [selectedVariantValue];

            if(otherSelectedValues !== null) {
                selectedVariantValues.push(...otherSelectedValues);
            }

            const variantProduct = this.product.variantProducts.find(variantProduct => arraysAreEqual(variantProduct.variants, selectedVariantValues))

            if(!variantProduct){
                continue;
            }

            let imageUrl:string = null;
            if(variantProduct.imageUrl){
                if(variantProduct.imageUrl.includes('?')){
                    imageUrl = `${variantProduct.imageUrl}&width=${this.IMAGE_WIDTH}&${this.IMAGE_MODE}`;
                } else {
                    imageUrl = `${variantProduct.imageUrl}?width=${this.IMAGE_WIDTH}&${this.IMAGE_MODE}`;
                }
            }

            const variantShortInfo = this._variantsShortInfo?.variantShortInfoList.find(variantShortInfo => arraysAreEqual(variantShortInfo.variantValues, selectedVariantValues));
            if(variantShortInfo){
                const variantName = option.getAttribute('data-name');
                if(imageUrl){
                    option.innerHTML = `<div><img src=" ${imageUrl}"/><span class="title">${variantName}</span></div>`;
                }

                if(variantShortInfo.discountPercent > 0) {
                    option.classList.add("dropdown-item-discount");
                    option.innerHTML += `<div><div class="info"><span class="discount">-${variantShortInfo.discountPercent}%</span><strong class="price">${variantShortInfo.priceString}</strong></div><span class="c-product-status ${variantProduct.stockCssClass}">${variantProduct.stockText}</span></div>`;
                } else {
                    option.innerHTML += `<div><div class="info"><strong class="price">${variantShortInfo.priceString}</strong></div><span class="c-product-status ${variantProduct.stockCssClass}">${variantProduct.stockText}</span></div>`;
                }

                if(variantShortInfo.isOutlet){
                    option.querySelector(".info").setAttribute("data-outlet",  this.translations.get('outlet'));
                    option.classList.add("dropdown-item-is-outlet");
                }
                if(variantProduct.stockCssClass === "sold-out") {
                    option.classList.add("dropdown-item-sold-out");
                }
            } else {
                const variantName = option.getAttribute('data-name');
                if(imageUrl){
                    option.innerHTML = `<div><img src=" ${imageUrl}"/><span class="title">${variantName}</span></div>`;
                }

                option.innerHTML += `<div><div class="info"></div><span class="c-product-status ${variantProduct.stockCssClass}">${variantProduct.stockText}</span></div>`;

                if(variantProduct.stockCssClass === "sold-out") {
                    option.classList.add("dropdown-item-sold-out");
                }
            }
        }
    }

    private _checkValuesToFill(){
        const self = this;
        self._valuesToFill.forEach((valuesToFill: Array<number>) => {
            self._fillVariantsInfo(valuesToFill);
        })
        this._valuesToFill = null;
    }
}
